import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from "react";
import { useIonToast } from "../../shared/IonToast/index.js";
import { useMyOrganizations } from "../../OrganizationPopover/hooks/useMyOrganizations.js";
import api from "../../../api.js";
const LAST_SEEN_KEY = "NOTIFICATION_LAST_SEEN";
const MyInvitesContext = createContext();
export function MyInvitesProvider({ children }) {
  const { fetchOrganizations } = useMyOrganizations();
  const { toastSuccess, toastError } = useIonToast();
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState(null);
  const [lastSeen, setLastSeen] = useState(
    () => Number(localStorage.getItem(LAST_SEEN_KEY)) || 0
  );
  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getMyInvites();
      setInvites(data);
      setError(null);
    } catch {
      setError("Could not fetch invites, please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);
  const accept = async (inviteId) => {
    const invite = invites.find((i) => i.id === inviteId);
    if (!invite) {
      toastError("Invite not found");
      return;
    }
    const orgName = invite.organization_name || "an organization";
    try {
      await api.acceptInvite(inviteId);
      setInvites((invites2) => invites2.filter((i) => i.id !== inviteId));
      await fetchOrganizations();
      toastSuccess(`Organization added! You\u2019ve joined ${orgName}.`);
    } catch (error2) {
      const message = error2?.message || "Cannot accept invite";
      toastError(`Error: ${message}`);
    }
  };
  const decline = async (inviteId) => {
    try {
      await api.declineInvite(inviteId);
      setInvites((invites2) => invites2.filter((i) => i.id !== inviteId));
      toastSuccess("Organization invite rejected.");
    } catch (error2) {
      const message = error2?.message || "Cannot decline invite";
      toastError(`Error: ${message}`);
    }
  };
  const markAllSeen = () => {
    const now = Date.now();
    setLastSeen(now);
    localStorage.setItem(LAST_SEEN_KEY, String(now));
  };
  const newCount = lastSeen === 0 ? invites.length : invites.some((i) => new Date(i.created_at).getTime() > lastSeen);
  return /* @__PURE__ */ React.createElement(
    MyInvitesContext.Provider,
    {
      value: {
        invites,
        fetchInvites,
        accept,
        decline,
        markAllSeen,
        newCount,
        hasNew: newCount > 0,
        loading,
        error
      }
    },
    children
  );
}
export const useMyInvites = () => useContext(MyInvitesContext);
